import React, { PureComponent } from "react";
import { branch } from "baobab-react/higher-order";
import PropTypes from "baobab-react/prop-types";

import autoBind from "react-autobind";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "react-bootstrap";

import * as actions from "../../actions/actions";
import { isFutureEnabled } from "../../utils/featureFlags";

const PREVIEW_POPOUT_CHANNEL_PREFIX = "ambifi-preview-popout-v1";
const PREVIEW_POPOUT_READY = "preview-popout-ready";
const PREVIEW_POPOUT_CLOSED = "preview-popout-closed";
const PREVIEW_POPOUT_SNAPSHOT = "preview-popout-snapshot";
const PREVIEW_POPOUT_COMMAND = "preview-popout-command";
const PREVIEW_UPDATE_MODE_LIVE = "live";
const PREVIEW_UPDATE_MODE_ON_REFRESH = "onRefresh";
const PREVIEW_UPDATE_MODE_MANUAL = "manual";
const PREVIEW_POPOUT_STATE_UPDATE_DELAY_MS = 250;
const PREVIEW_POPOUT_TEXT_STATE_UPDATE_DELAY_MS = 850;
const PREVIEW_POPOUT_TEXT_FIELDS = new Set([
	"comments",
	"commentsAuthor",
	"commentsPrint",
	"commentsTemplate",
	"label1",
	"label1AlexaPlain",
	"label1AlexaRich",
	"label1Print",
	"label2",
	"label2AlexaPlain",
	"label2AlexaRich",
	"label2Print",
	"name",
	"nameAlexaPlain",
	"nameAlexaRich",
	"namePrint",
	"subtitle",
	"title"
]);

function createPreviewPopoutChannelName(channelId) {
	return `${PREVIEW_POPOUT_CHANNEL_PREFIX}:${channelId}`;
}

function clonePreviewPopoutPayload(value) {
	if (value === undefined) {
		return undefined;
	}

	try {
		return JSON.parse(JSON.stringify(value));
	} catch (_err) {
		return value;
	}
}

class PreviewWrapper extends PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		this.previewRootRef = React.createRef();
		this.popoutWindow = null;
		this.popoutWindowPollHandle = null;
		this.popoutHandshakeHandle = null;
		this.popoutChannel = null;
		this.pendingPreviewStateUpdateTimer = null;
		this.pendingPreviewStateUpdates = [];
		this.previewTreeUpdateListenerActive = false;
		this.state = {
			popoutOpen: false,
			popoutReady: false,
			popoutChannelId: ""
		};
	}

	componentDidUpdate(_prevProps, prevState) {
		if (this.state.popoutOpen !== true) {
			return;
		}

		if (prevState.popoutReady !== this.state.popoutReady && this.state.popoutReady === true) {
			this.pushPreviewPopoutSnapshot();
		}
	}

	componentWillUnmount() {
		this.teardownPreviewPopout(false, true);
	}

	setPreviewPopoutOpenState(isOpen) {
		this.context.tree.set(["appState", "editor", "previewPopoutOpen"], isOpen === true);
	}

	refreshPreview() {
		actions.previewChecklistInline();
	}

	refreshPreviewStateUpdates() {
		actions.applyPreviewStateUpdates(this.context.tree);
	}

	needsInitialPreviewBuild() {
		const previewChecklist = this.context.tree ? (this.context.tree.get(["appState", "previewChecklist"]) || {}) : {};
		const checklistId = previewChecklist && previewChecklist.checklist ? previewChecklist.checklist.id : "";
		return !checklistId || previewChecklist.showInline !== true;
	}

	togglePreviewPopout() {
		if (this.state.popoutOpen) {
			this.teardownPreviewPopout(true);
		} else {
			this.openPreviewPopout();
		}
	}

	handlePreviewPopoutClosed() {
		this.teardownPreviewPopout(false);
	}

	getPreviewPopoutUrl(channelId) {
		const url = new URL(window.location.href);
		url.pathname = "/preview-popout";
		url.search = `?channel=${encodeURIComponent(channelId)}`;
		url.hash = "";
		return url.toString();
	}

	openPreviewPopout() {
		if (this.needsInitialPreviewBuild()) {
			this.refreshPreview();
		}

		const channelId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
		const popupUrl = this.getPreviewPopoutUrl(channelId);
		const popupName = "ambifi-cloud-preview-popout";
		const popupFeatures = [
			"popup=yes",
			"toolbar=no",
			"location=no",
			"status=no",
			"menubar=no",
			"scrollbars=yes",
			"resizable=yes",
			`width=${window.screen && window.screen.availWidth ? window.screen.availWidth : window.innerWidth}`,
			`height=${window.screen && window.screen.availHeight ? window.screen.availHeight : window.innerHeight}`,
			"left=0",
			"top=0"
		].join(",");

		this.popoutWindow = window.open(popupUrl, popupName, popupFeatures);
		if (!this.popoutWindow) {
			window.alert("Unable to open preview pop out. Please allow popups for this site.");
			return;
		}

		this.popoutChannel = this.createPreviewPopoutChannel(channelId);
		this.popoutWindow.focus();

		this.setState({
			popoutOpen: true,
			popoutReady: false,
			popoutChannelId: channelId
		}, () => {
			this.setPreviewPopoutOpenState(true);
			this.startPreviewPopoutTreeListener();
			this.startPreviewPopoutClosedPoll();
			this.startPreviewPopoutHandshake();
		});
	}

	createPreviewPopoutChannel(channelId) {
		if (typeof window.BroadcastChannel !== "function") {
			return null;
		}

		const channel = new window.BroadcastChannel(createPreviewPopoutChannelName(channelId));
		channel.onmessage = (event) => {
			const message = event && event.data ? event.data : null;
			if (!message || !message.type) {
				return;
			}

			if (message.type === PREVIEW_POPOUT_READY) {
				this.setState({ popoutReady: true }, () => {
					this.clearPreviewPopoutHandshake();
					this.pushPreviewPopoutSnapshot();
				});
			} else if (message.type === PREVIEW_POPOUT_CLOSED) {
				this.handlePreviewPopoutClosed();
			} else if (message.type === PREVIEW_POPOUT_COMMAND) {
				this.handlePreviewPopoutCommand(message);
			}
		};

		return channel;
	}

	handlePreviewPopoutCommand(message) {
		const command = message && message.command ? message.command : "";
		const payload = message && message.payload ? message.payload : {};

		if (command === "toggleVisible") {
			actions.togglePreviewVisible(this.context.tree);
		} else if (command === "toggleLive") {
			actions.togglePreviewLive(this.context.tree);
		} else if (command === "refresh") {
			this.refreshPreview();
		} else if (command === "refreshStateUpdates") {
			this.refreshPreviewStateUpdates();
		} else if (command === "returnPreview") {
			this.teardownPreviewPopout(true);
		} else if (command === "setPreviewField") {
			if (payload && payload.field) {
				this.context.tree.set(["appState", "preview", payload.field], payload.value);
			}
		} else if (command === "setPreviewConfig") {
			if (payload && payload.preview) {
				this.context.tree.set(["appState", "preview"], payload.preview);
			}
		} else if (command === "swapPreviewDimensions") {
			const currentWidth = this.context.tree.get(["appState", "preview", "previewWidth"]);
			const currentHeight = this.context.tree.get(["appState", "preview", "previewHeight"]);
			this.context.tree.set(["appState", "preview", "previewWidth"], currentHeight);
			this.context.tree.set(["appState", "preview", "previewHeight"], currentWidth);
		}
	}

	startPreviewPopoutClosedPoll() {
		this.clearPreviewPopoutClosedPoll();
		this.popoutWindowPollHandle = window.setInterval(() => {
			if (!this.popoutWindow || this.popoutWindow.closed) {
				this.handlePreviewPopoutClosed();
			}
		}, 500);
	}

	clearPreviewPopoutClosedPoll() {
		if (this.popoutWindowPollHandle) {
			window.clearInterval(this.popoutWindowPollHandle);
			this.popoutWindowPollHandle = null;
		}
	}

	startPreviewPopoutHandshake() {
		this.clearPreviewPopoutHandshake();
		this.popoutHandshakeHandle = window.setInterval(() => {
			this.pushPreviewPopoutSnapshot();
		}, 750);
	}

	clearPreviewPopoutHandshake() {
		if (this.popoutHandshakeHandle) {
			window.clearInterval(this.popoutHandshakeHandle);
			this.popoutHandshakeHandle = null;
		}
	}

	startPreviewPopoutTreeListener() {
		if (this.previewTreeUpdateListenerActive || !this.context.tree || !this.context.tree.on) {
			return;
		}

		this.context.tree.on("update", this.handlePreviewTreeUpdate);
		this.previewTreeUpdateListenerActive = true;
	}

	clearPreviewPopoutTreeListener() {
		if (!this.previewTreeUpdateListenerActive || !this.context.tree || !this.context.tree.off) {
			this.previewTreeUpdateListenerActive = false;
			return;
		}

		this.context.tree.off("update", this.handlePreviewTreeUpdate);
		this.previewTreeUpdateListenerActive = false;
	}

	handlePreviewTreeUpdate(event) {
		if (this.state.popoutOpen !== true || !this.popoutChannel) {
			return;
		}

		const transactions = event && event.data && Array.isArray(event.data.transaction) ? event.data.transaction : [];
		const hasFullSnapshotUpdate = transactions.some((transaction) => {
			const path = Array.isArray(transaction && transaction.path) ? transaction.path : [];
			return (
				path[0] === "drive" ||
				(path[0] === "appState" && path[1] === "preview") ||
				(path[0] === "appState" && path[1] === "previewChecklist")
			);
		});

		if (hasFullSnapshotUpdate) {
			this.pushPreviewPopoutSnapshot();
			return;
		}

		const hasStateUpdates = transactions.some((transaction) => {
			const path = Array.isArray(transaction && transaction.path) ? transaction.path : [];
			return path[0] === "appState" && path[1] === "stateUpdates";
		});

		if (hasStateUpdates) {
			this.pushPreviewPopoutStateUpdates();
		}
	}

	clearPendingPreviewStateUpdates() {
		if (this.pendingPreviewStateUpdateTimer) {
			window.clearTimeout(this.pendingPreviewStateUpdateTimer);
			this.pendingPreviewStateUpdateTimer = null;
		}
		this.pendingPreviewStateUpdates = [];
	}

	pushPreviewPopoutSnapshot() {
		if (!this.popoutChannel) {
			return;
		}

		this.clearPendingPreviewStateUpdates();

		this.popoutChannel.postMessage({
			type: PREVIEW_POPOUT_SNAPSHOT,
			payload: {
				preview: clonePreviewPopoutPayload(this.getPreviewData()),
				previewChecklist: clonePreviewPopoutPayload(this.getPreviewChecklistData()),
				stateUpdates: clonePreviewPopoutPayload(this.getPreviewStateUpdatesData()),
				drive: clonePreviewPopoutPayload({
					guidToResource: this.getDriveGuidToResourceData()
				})
			}
		});
	}

	getPreviewData() {
		return this.context.tree ? this.context.tree.get(["appState", "preview"]) : this.props.preview;
	}

	getPreviewChecklistData() {
		return this.context.tree ? this.context.tree.get(["appState", "previewChecklist"]) : {};
	}

	getPreviewStateUpdatesData() {
		return this.context.tree ? (this.context.tree.get(["appState", "stateUpdates"]) || []) : [];
	}

	getDriveGuidToResourceData() {
		const drive = this.context.tree ? this.context.tree.get(["drive"]) : {};
		return drive && drive.guidToResource ? drive.guidToResource : {};
	}

	pushPreviewPopoutStateUpdates() {
		if (!this.popoutChannel) {
			return;
		}

		const stateUpdates = this.getPreviewStateUpdatesData();
		if (!Array.isArray(stateUpdates) || stateUpdates.length === 0) {
			return;
		}

		const canCoalesce = stateUpdates.every((stateUpdate) => stateUpdate && (stateUpdate.transactionType === "set" || stateUpdate.transactionType === "unset"));
		if (!canCoalesce) {
			this.flushPendingPreviewStateUpdates();
			this.postPreviewPopoutStateUpdates(stateUpdates);
			return;
		}

		stateUpdates.forEach((stateUpdate) => {
			const updateKey = this.getPreviewStateUpdateKey(stateUpdate);
			const existingIndex = this.pendingPreviewStateUpdates.findIndex((pendingUpdate) => this.getPreviewStateUpdateKey(pendingUpdate) === updateKey);
			if (existingIndex >= 0) {
				this.pendingPreviewStateUpdates[existingIndex] = stateUpdate;
			} else {
				this.pendingPreviewStateUpdates.push(stateUpdate);
			}
		});

		if (this.pendingPreviewStateUpdateTimer) {
			window.clearTimeout(this.pendingPreviewStateUpdateTimer);
		}
		this.pendingPreviewStateUpdateTimer = window.setTimeout(() => {
			this.flushPendingPreviewStateUpdates();
		}, this.getPreviewStateUpdateDelay(stateUpdates));
	}

	getPreviewStateUpdateDelay(stateUpdates = []) {
		const hasTextUpdate = stateUpdates.some((stateUpdate) => {
			const path = Array.isArray(stateUpdate && stateUpdate.path) ? stateUpdate.path : [];
			const propertyName = path.length > 0 ? path[path.length - 1] : "";
			return stateUpdate && stateUpdate.transactionType === "set" && PREVIEW_POPOUT_TEXT_FIELDS.has(propertyName);
		});

		return hasTextUpdate ? PREVIEW_POPOUT_TEXT_STATE_UPDATE_DELAY_MS : PREVIEW_POPOUT_STATE_UPDATE_DELAY_MS;
	}

	getPreviewStateUpdateKey(stateUpdate = {}) {
		return JSON.stringify({
			path: stateUpdate.path || [],
			transactionType: stateUpdate.transactionType || "",
			type: stateUpdate.type || ""
		});
	}

	flushPendingPreviewStateUpdates() {
		if (this.pendingPreviewStateUpdateTimer) {
			window.clearTimeout(this.pendingPreviewStateUpdateTimer);
			this.pendingPreviewStateUpdateTimer = null;
		}

		const stateUpdates = this.pendingPreviewStateUpdates;
		this.pendingPreviewStateUpdates = [];
		if (!Array.isArray(stateUpdates) || stateUpdates.length === 0) {
			return;
		}

		this.postPreviewPopoutStateUpdates(stateUpdates);
	}

	postPreviewPopoutStateUpdates(stateUpdates) {
		if (!this.popoutChannel) {
			return;
		}

		this.popoutChannel.postMessage({
			type: PREVIEW_POPOUT_SNAPSHOT,
			payload: {
				stateUpdates: clonePreviewPopoutPayload(stateUpdates)
			}
		});
	}

	teardownPreviewPopout(closeWindow, suppressStateUpdate = false) {
		this.clearPreviewPopoutHandshake();
		this.clearPreviewPopoutClosedPoll();
		this.clearPreviewPopoutTreeListener();
		this.clearPendingPreviewStateUpdates();

		if (this.popoutChannel) {
			try {
				this.popoutChannel.close();
			} catch (_err) {
				// Ignore channel close failures.
			}
			this.popoutChannel = null;
		}

		if (closeWindow && this.popoutWindow && !this.popoutWindow.closed) {
			try {
				this.popoutWindow.close();
			} catch (_err) {
				// Ignore popup close failures.
			}
		}

		this.popoutWindow = null;

		if (!suppressStateUpdate && (this.state.popoutOpen || this.state.popoutReady || this.state.popoutChannelId !== "")) {
			this.setPreviewPopoutOpenState(false);
			this.setState({
				popoutOpen: false,
				popoutReady: false,
				popoutChannelId: ""
			});
		}
	}

	// Doing at runtime...could potentially do first time loads up in state
	render() {
		const previewPopoutEnabled = this.props.allowPopout && isFutureEnabled(this.props.urlParams || {});
		const previewUpdateMode = this.getPreviewUpdateMode();
		const previewUpdateModeLabel = actions.getPreviewUpdateModeLabel(previewUpdateMode);
		const previewFluxIconStyle = previewUpdateMode === PREVIEW_UPDATE_MODE_ON_REFRESH ? { color: "#f0ad4e" } : {};
		const previewFluxIconPrefix = previewUpdateMode === PREVIEW_UPDATE_MODE_LIVE ? "fas" : "far";
		const canApplyStateUpdates = previewUpdateMode === PREVIEW_UPDATE_MODE_ON_REFRESH;
		const previewViewportStyle = {
			overflow: "auto",
			height: "100%",
			backgroundColor: "#666666",
			display: "flex",
			flexDirection: "column",
			minHeight: 0
		};
		const previewScaledSurfaceStyle = {
			width: (this.props.preview.previewWidth.endsWith("%") || this.props.preview.previewWidth.endsWith("px")) ? this.props.preview.previewWidth : this.props.preview.previewWidth + "px",
			height: (this.props.preview.previewHeight.endsWith("%") || this.props.preview.previewHeight.endsWith("px")) ? this.props.preview.previewHeight : this.props.preview.previewHeight + "px",
			transformOrigin: "top left",
			transform: "scale(" + Number(this.props.preview.previewScale) / 100 + ")",
			flex: "0 0 auto"
		};
		const previewSurface =
			<div style={previewViewportStyle}>
				<div style={previewScaledSurfaceStyle}>
					{this.props.children}
				</div>
			</div>;
		const previewPlaceholder = previewPopoutEnabled && this.state.popoutOpen ? (
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 0, backgroundColor: "#666666", color: "white", padding: "20px", textAlign: "center" }}>
				<div>
					<div style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "8px" }}>Preview popped out</div>
					<div style={{ fontSize: "12px", opacity: 0.85 }}>Use Return Preview in the toolbar to bring it back into the editor.</div>
				</div>
			</div>
		) : previewSurface;
		const compactModalToolbar = this.props.compact && !this.props.showTitle && !this.props.showActions;
		const toolbarHeight = this.props.compact ? "50px" : "auto";
		const toolbarMinHeight = this.props.compact ? "50px" : "75px";
		const toolbarTitleStyle = {
			marginLeft: "0px",
			display: "flex",
			flexDirection: "column",
			flex: "0 0 auto",
			minWidth: "120px"
		};
		const toolbarControlsStyle = {
			display: "flex",
			alignItems: "center",
			justifyContent: compactModalToolbar ? "center" : "flex-end",
			gap: "10px",
			flexWrap: "wrap",
			minWidth: 0,
			flex: compactModalToolbar ? "0 1 auto" : "1 1 auto"
		};
		const resolutionGroupStyle = {
			marginTop: "0px",
			display: "flex",
			flexDirection: "row",
			alignItems: "flex-end",
			justifyContent: "center",
			gap: "10px",
			flex: "0 1 auto",
			minWidth: "285px",
			flexWrap: "nowrap"
		};
		const zoomGroupStyle = {
			marginTop: "0px",
			marginLeft: this.props.compact ? "15px" : "0px",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			flex: "0 0 auto"
		};
		const actionGroupStyle = {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "flex-end",
			gap: "5px",
			flex: "0 1 auto",
			flexWrap: "wrap"
		};
		const previewPopoutButton = previewPopoutEnabled ? (
			<Button
				bsSize="small"
				onClick={this.togglePreviewPopout}
				title={this.state.popoutOpen ? "Return preview to this window" : "Open preview in a separate window"}
				aria-label={this.state.popoutOpen ? "Return preview to this window" : "Open preview in a separate window"}
			>
				<FontAwesomeIcon size="lg" icon={this.state.popoutOpen ? "window-restore" : "external-link-alt"} />
			</Button>
		) : null;

		return (
			<div ref={this.previewRootRef} style={{ position: "relative", width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
				<div style={{ backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: this.props.compact ? "center" : "space-between", gap: "10px", flexWrap: this.props.compact ? "nowrap" : "wrap", width: "100%", padding: "5px", height: toolbarHeight, minHeight: toolbarMinHeight, boxSizing: "border-box", overflow: "visible" }}>
					{this.props.showTitle &&
						<div style={toolbarTitleStyle}>
							<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
								<div style={{ fontSize: "18px", fontWeight: "bold", display: "inline-block" }}>Preview</div>
								{previewPopoutButton}
							</div>
							<div style={{ fontSize: "12px", color: "#444444" }}>{`(${previewUpdateModeLabel})`}</div>
						</div>
					}
					<div style={toolbarControlsStyle}>
						<div style={resolutionGroupStyle}>
							<div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
								<div style={{ fontSize: "10px", color: "#444444" }}><b>Resolution</b> <span style={{ color: "#444444", fontSize: "10px" }}>(% or px)</span></div>
								<div style={{ display: "flex" }}>
									<input disabled={this.props.preview.resolutionDisabled} style={{ borderRadius: "5px", border: "1px solid #cccccc", width: "50px" }} type="text" value={this.props.preview.previewWidth} onChange={(e) => {
										this.context.tree.set(["appState", "preview", "previewWidth"], e.target.value);
									}}></input>
									<div style={{ marginLeft: "3px", marginRight: "3px" }}>x</div>
									<input disabled={this.props.preview.resolutionDisabled} style={{ borderRadius: "5px", border: "1px solid #cccccc", width: "50px" }} type="text" value={this.props.preview.previewHeight} onChange={(e) => {
										this.context.tree.set(["appState", "preview", "previewHeight"], e.target.value);
									}}></input>
									<div style={{ marginLeft: "7px", transform: "rotate(-45deg)", cursor: "pointer" }} onClick={() => {
										this.context.tree.set(["appState", "preview", "previewWidth"], this.props.preview.previewHeight);
										this.context.tree.set(["appState", "preview", "previewHeight"], this.props.preview.previewWidth);
									}}>
										<FontAwesomeIcon size="sm" icon={["fas", "mobile-alt"]} />
									</div>

								</div>
							</div>
							<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginTop: "0px", marginBottom: "0px", width: "135px", flexShrink: 0 }} value={this.props.preview.resolution} onChange={(e) => {
								const resValue = e.target.value;
								if (resValue === "responsive") {
									this.context.tree.set(["appState", "preview"], {
										resolution: resValue,
										previewScale: this.context.tree.get(["appState", "preview", "previewScale"]),
										previewWidth: "100%",
										previewHeight: "100%",
										resolutionDisabled: false
									});
								} else if (resValue === "custom") {
									this.context.tree.set(["appState", "preview"], {
										resolution: resValue,
										previewScale: this.context.tree.get(["appState", "preview", "previewScale"]),
										previewWidth: "480",
										previewHeight: "640",
										resolutionDisabled: false
									});
								} else {
									const resParts = resValue.split(",");
									this.context.tree.set(["appState", "preview"], {
										resolution: resValue,
										previewScale: this.context.tree.get(["appState", "preview", "previewScale"]),
										previewWidth: resParts[0],
										previewHeight: resParts[1],
										resolutionDisabled: true
									});
								}
							}}>
								<option value="responsive">Responsive</option>
								<option value="custom">Custom</option>
								<option value="360,640">Moto G4</option>
								<option value="360,640">Galaxy S5</option>
								<option value="411,731">Pixel 2</option>
								<option value="411,823">Pixel 2 XL</option>
								<option value="320,568">iPhone 5/SE</option>
								<option value="375,667">iPhone 6/7/8</option>
								<option value="414,736">iPhone 6/7/8 Plus</option>
								<option value="375,812">iPhone X</option>
								<option value="768,1024">iPad</option>
								<option value="1024,1366">iPad Pro 12.9</option>
								<option value="540,720">Surface Duo</option>
								<option value="280,653">Galaxy Fold</option>
								<option value="480,480">Vuxiz Blade</option>
								<option value="854,480">Vuxiz M400/4000</option>
							</select>
						</div>
							<div style={zoomGroupStyle}>
								<div style={{ color: "#444444", fontSize: "10px", fontWeight: "bold" }}>Zoom</div>
								<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginRight: "5px", marginBottom: "0px" }} value={this.props.preview.previewScale} onChange={(e) => {
									this.context.tree.set(["appState", "preview", "previewScale"], e.target.value);
							}}>
								<option value="25">25%</option>
								<option value="33">33%</option>
								<option value="50">50%</option>
								<option value="66">66%</option>
								<option value="75">75%</option>
								<option value="100">100%</option>
								<option value="125">125%</option>
								<option value="150">150%</option>
									<option value="200">200%</option>
								</select>
							</div>
						</div>
							{this.props.showActions &&
								<div style={actionGroupStyle}>
									<Button onClick={() => this.props.dispatch(actions.togglePreviewVisible)}><FontAwesomeIcon size="lg" icon={this.props.previewAllVisible ? "eye" : "eye-slash"} /></Button>
									<Button
										onClick={() => this.props.dispatch(actions.togglePreviewLive)}
										title={`Preview mode: ${previewUpdateModeLabel}. Click to cycle modes.`}
									>
										<FontAwesomeIcon size="2x" icon={[previewFluxIconPrefix, "flux-capacitor"]} style={previewFluxIconStyle} />
									</Button>
									{canApplyStateUpdates &&
										<Button
											onClick={this.refreshPreviewStateUpdates}
											title={this.props.previewStateUpdatesPending ? "Apply pending preview updates" : "No pending preview updates"}
											disabled={!this.props.previewStateUpdatesPending}
										>
											<FontAwesomeIcon size="lg" icon="bolt" />
										</Button>
									}
									<Button onClick={this.refreshPreview} title="Full preview refresh"><FontAwesomeIcon size="lg" icon="sync" /></Button>
									{previewPopoutEnabled && !this.props.showTitle && previewPopoutButton}
									</div>
								}
					</div>
					<div style={{ flex: 1, minHeight: 0 }}>
						{previewPlaceholder}
					</div>
				</div>
				);
	}

	getPreviewUpdateMode() {
		if (this.props.previewUpdateMode === PREVIEW_UPDATE_MODE_LIVE || this.props.previewUpdateMode === PREVIEW_UPDATE_MODE_ON_REFRESH || this.props.previewUpdateMode === PREVIEW_UPDATE_MODE_MANUAL) {
			return this.props.previewUpdateMode;
		}
		return this.props.previewLive ? PREVIEW_UPDATE_MODE_LIVE : PREVIEW_UPDATE_MODE_MANUAL;
	}
}

PreviewWrapper.contextTypes = {
	tree: PropTypes.baobab
};

export default branch({
	preview: ["appState", "preview"],
	previewUpdateMode: ["appState", "previewChecklist", "updateMode"],
	previewStateUpdatesPending: ["appState", "previewChecklist", "stateUpdatesPending"],
	previewLive: ["appState", "previewChecklist", "live"],
	previewAllVisible: ["appState", "previewChecklist", "allVisible"],
	urlParams: ["appState", "urlParams"]
}, PreviewWrapper);
